// Résolution de source média rétrocompatible (ADR 0005, §5).
//
// Trois familles de références coexistent dans les POI historiques :
// - les entrées normalisées `marker.media` (URL authentifiée) ;
// - les valeurs historiques complètes stockées dans les champs legacy
//   (`image`, `video`, `audio`) : URL GitHub/raw absolue, chemin `/...` ou
//   data-URL Base64. Elles doivent être rendues telles quelles, sans
//   reconstruction depuis un identifiant et un nom ;
// - les noms seuls (`imageName`, etc.) qui n'ont jamais eu de valeur
//   complète : pour eux seulement, l'URL GitHub historique est reconstruite
//   à l'identique de l'existant.
//
// Ce module centralise la résolution pour l'édition comme la consultation,
// afin que les vues image/video/audio ne puissent plus diverger.

const GITHUB_RAW_BASES = {
  image: "https://raw.githubusercontent.com/mcharef22/images/main/",
  video: "https://raw.githubusercontent.com/mcharef22/videos/main/",
  audio: "https://raw.githubusercontent.com/mcharef22/audios/main/",
};

const isCompleteSource = (value) =>
  typeof value === "string" &&
  value.length > 0 &&
  (value.startsWith("data:") ||
    value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("/"));

const legacyNameAt = (marker, mediaType, index) => {
  const nameField = `${mediaType}Name`;
  const names = marker?.[nameField];
  return Array.isArray(names) ? names[index] : undefined;
};

// Entrée normalisée correspondant à une référence legacy, identifiée par son
// nom d'affichage (originalName, sinon fileName). Les données chargées depuis
// le serveur portent une URL authentifiée déjà construite par l'API.
export const findServerMediaByName = (marker, mediaType, name) => {
  if (!name) return undefined;
  return (marker?.media || []).find(
    (media) =>
      media.mediaType === mediaType &&
      (media.originalName === name || media.fileName === name)
  );
};

export const resolveMediaSource = (
  marker,
  mediaType,
  { value, name, index }
) => {
  // 1. Valeur locale d'édition (aperçu data-URL) ou référence legacy
  //    complète : rendue telle quelle.
  if (isCompleteSource(value)) {
    return value;
  }
  // 2. Média normalisé authentifié.
  const mediaName = name ?? legacyNameAt(marker, mediaType, index);
  const serverMedia = findServerMediaByName(marker, mediaType, mediaName);
  if (serverMedia?.url) {
    return serverMedia.url;
  }
  // 3. Nom seul, sans référence complète ni entrée normalisée : URL GitHub
  //    historique reconstruite (dernier recours inchangé).
  return `${GITHUB_RAW_BASES[mediaType]}${marker?.id ?? ""}${mediaName ?? ""}`;
};

// Liste de valeurs prêtes à afficher en consultation : chaque entrée
// normalisée du type demandé est rendue par son URL authentifiée, sans
// exiger la présence des champs legacy.
export const serverMediaValues = (marker, mediaType) =>
  (marker?.media || [])
    .filter((media) => media.mediaType === mediaType && media.url)
    .map((media) => ({
      url: media.url,
      name: media.originalName || media.fileName,
    }));
